import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { CREDENTIALS, DATASOURCE } from '../api-client/constants'
import { removeLocalItem } from '../local-client/local-client'
import styles from '../component-styles/website-picker.module.scss'
import {
  AdobeAccount,
  AdobeSuite,
  reconnectAA,
  getAdobeReportSuites,
} from '../api-client/settings-client'
import { getSelectedAccountAsync, switchAccountAsync } from '../store/actions'

// import { connectFirstAccount } from '../api-client/setup-client'
import SelectBoxTypeahead from './select-box-typeahead'
import { getItemByKeyValue } from '../helpers'
import { Label } from './input'
import { FormRow, LabelSlot, FieldSlot } from './row'
import ConfirmationButtons from './confirmation-buttons'
// import { UserState } from '../store/user-reducer'
// import { SiteState } from '../store/site-reducer'
import Modal, { ModalBody, ModalHead, ModalFooter } from './modal'
import { Preloader } from './loader'

interface Props {
  currentAccount: string
  onToggle: () => void
  selectedAccountId: string
}

export default function ReconnectAAModal({
  currentAccount,
  onToggle,
  selectedAccountId,
}: Props) {
  const dispatch = useDispatch()

  const [accountData, setAccountData] = useState<AdobeAccount[]>([])
  const [company, setCompany] = useState<AdobeAccount | null>(null)
  const [reportSuite, setReportSuite] = useState<AdobeSuite | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [submitError, setSubmitError] = useState('')
  // const [loadingSubmit, setLoadingSubmit] = useState(false)
  // const user = useSelector((state: any): UserState => state.user)
  // const { details } = user
  // const { selected } = useSelector(({ site }: { site: SiteState }) => site)
  // const accountList = [...(details?.accountList || [])]

  const oauth = useSelector((state: any): any => state.oauth)
  const { credentials, loading } = oauth

  useEffect(() => {
    dispatch(getSelectedAccountAsync())
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!loading && (credentials === '' || accountData.length > 0)) return
    ;(async () => {
      const accounts = await getAdobeReportSuites({ credentials })
      if (accounts?.length > 0) {
        setAccountData(accounts)
      }
      setLoadingStatus(false)
    })()
    // eslint-disable-next-line
  }, [accountData, setAccountData, credentials])

  const handleSubmit = async () => {
    if (!company || !reportSuite) return
    const { adobeCompanyName, adobeGlobalCompanyId, adobeImsOrgId } = company
    const { adobeReportSuiteId, adobeReportSuiteName } = reportSuite
    const data = {
      uplifterAccountID: selectedAccountId,
      adobeCompanyName,
      adobeGlobalCompanyId,
      adobeImsOrgId,
      adobeReportSuiteId,
      adobeReportSuiteName,
      credentials,
    }

    const res = await reconnectAA(data)
    if (!res) {
      setSubmitError(`Cannot reconnect with your Adobe account`)
      return
    }
    setSubmitError('')
    onToggle()
    if (currentAccount) {
      removeLocalItem([CREDENTIALS, DATASOURCE])
      dispatch(switchAccountAsync(currentAccount))
    }
    // refreshAccount(true)

    // const isFirstAccountToConnect =
    //   accountList.length < 1 ||
    //   (accountList.length === 1 && accountList[0].preSetup)
    // let success
    // setLoadingSubmit(true)
    // if (isFirstEnterpriseAccountToConnect) {
    //   success = await addFirstEnterpriseAccount({
    //     ...mapAccount(account),
    //     ...mapProperty(property),
    //     ...mapView(view),
    //     credentials,
    //   })
    // } else if (isFirstAccountToConnect) {
    //   success = await connectFirstAccount({
    //     ...mapAccount(account),
    //     ...mapProperty(property),
    //     ...mapView(view),
    //     credentials,
    //   })
    // } else {
    //   success = await addAccount({
    //     ...mapAccount(account),
    //     ...mapProperty(property),
    //     ...mapView(view),
    //     credentials,
    //   })
    // }
    // setLoadingSubmit(false)
    // if (!success) {
    //   setSubmitError('Sorry, we could not add this account. Please try again.')
    //   return
    // }

    // if (onClick) {
    //   onClick()
    //   return
    // }

    // window.location.assign('/track/create-campaign-codes')
  }

  return (
    <Modal narrow type="white" active toggleActive={onToggle}>
      <ModalHead>
        <h3>Connect your website</h3>
      </ModalHead>
      <ModalBody>
        <div>
          {loadingStatus ? (
            <div className={styles.preloader}>
              <Preloader style={{ width: 75, height: 60 }} />
            </div>
          ) : (
            <>
              <FormRow last>
                <LabelSlot column>
                  <Label id="adobe-comp">Company</Label>
                </LabelSlot>
                <FieldSlot column>
                  <SelectBoxTypeahead
                    id="adobe-comp"
                    key={`adobe-comp${accountData.length}`}
                    disabled={accountData.length === 0}
                    placeholder="Choose your company"
                    keyName="adobeCompanyName"
                    keyValue="adobeGlobalCompanyId"
                    onInputChange={(val: string): void => {
                      const acc = getItemByKeyValue(
                        accountData,
                        'adobeGlobalCompanyId',
                        val,
                      )
                      setCompany(acc === -1 ? null : acc)
                      setReportSuite(null)
                    }}
                    items={accountData}
                  />
                </FieldSlot>
              </FormRow>
              <FormRow last>
                <LabelSlot column>
                  <Label id="report-suite">Report suite</Label>
                </LabelSlot>
                <FieldSlot column>
                  <SelectBoxTypeahead
                    key={`report-suite-${JSON.stringify(company)}`}
                    id="report-suite"
                    disabled={!company}
                    placeholder="Choose your report suite"
                    keyName="adobeReportSuiteName"
                    keyValue="adobeReportSuiteId"
                    onInputChange={(val: string): void => {
                      const prop = company
                        ? getItemByKeyValue(
                            company.reportSuites,
                            'adobeReportSuiteId',
                            val,
                          )
                        : null
                      setReportSuite(prop === -1 ? null : prop)
                    }}
                    items={company?.reportSuites || []}
                  />
                </FieldSlot>
              </FormRow>
            </>
          )}
        </div>
        {submitError && <p className={styles.error}>{submitError}</p>}
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          disabled={!!(!company || !reportSuite)}
          type="simple"
          onYes={(): void => {
            handleSubmit()
          }}
          onNo={(): void => {
            if (onToggle) {
              onToggle()
            }
          }}
          yesLabel="Connect website"
          noLabel="Go back"
        />
      </ModalFooter>
    </Modal>
  )
}
